<template>
    <div class="marquee" :style="{ height: height + 'px' }">
        <div
            ref="track"
            class="marquee-track"
            :style="{ '--marquee-distance': marqueeDistance + 'px' }"
            :class="{ 'paused': paused }"
            @mouseenter="handleMouseEnter"
            @mouseleave="handleMouseLeave"
        >
            <div v-for="(img, idx) in doubledImages" :key="idx" class="marquee-item">
                <img :src="img" :alt="`sponsor-${idx}`">
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        name: 'MarqueeSponsors',
        props: {
            images: {
                type: Array,
                default: () => []
            },
            overlap: {
                type: Number,
                default: 2
            },
            speed: {
                // seconds to complete one loop (lower = faster)
                type: Number,
                default: 18
            },
            height: {
                type: Number,
                default: 80
            }
        },
        data() {
            return {
                paused: false,
                marqueeDistance: 0,
                repeatTimes: 1,
                offset: 0,
                lastTs: null,
                rafId: null
            }
        },
        computed: {
            doubledImages() {
                // build a repeated set that will be duplicated for seamless loop
                const set = []
                for (let i = 0; i < (this.repeatTimes || 1); i++) {
                    set.push(...this.images)
                }
                return [...set, ...set]
            }
        },
        mounted() {
            // compute pixel distance equal to the width of one image-set (half of track)
            this.$nextTick(() => {
                // wait for images to load before measuring to avoid layout shifts that break the loop
                this.computeMarqueeDistance().then(() => {
                    this.startAnimation()
                })
                window.addEventListener('resize', this.computeMarqueeDistance)
            })
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.computeMarqueeDistance)
            this.stopAnimation()
        },
        methods: {
            waitForImagesLoaded(timeout = 3000) {
                const track = this.$refs.track
                if (!track) return Promise.resolve()
                const imgs = Array.from(track.querySelectorAll('img'))
                if (!imgs.length) return Promise.resolve()

                return new Promise(resolve => {
                    let remaining = imgs.length
                    const onDone = () => {
                        remaining--
                        if (remaining <= 0) resolve()
                    }

                    imgs.forEach(img => {
                        if (img.complete && img.naturalWidth !== 0) {
                            onDone()
                        } else {
                            img.addEventListener('load', onDone, { once: true })
                            img.addEventListener('error', onDone, { once: true })
                        }
                    })

                    // safety timeout in case loads hang
                    setTimeout(resolve, timeout)
                })
            },

            async computeMarqueeDistance() {
                const track = this.$refs.track
                if (!track) return

                // stop animation while we measure to avoid visual jumps
                this.stopAnimation()

                // wait for images to load to avoid measuring too-small width
                await this.waitForImagesLoaded(3000)

                // scrollWidth is width of duplicated content; half of it is the single set width
                let total = track.scrollWidth || 0
                let half = Math.floor(total / 2)

                // if a single set is narrower than the container, increase repeatTimes so it fills the container
                const containerWidth = this.$el ? this.$el.clientWidth : 0
                if (half < containerWidth) {
                    // compute required repeats so that single set >= containerWidth
                    const singleSetWidth = half || 1
                    const needed = Math.ceil(containerWidth / singleSetWidth)
                    // update repeatTimes and re-measure
                    this.repeatTimes = Math.max(needed, 1)
                    await this.$nextTick()
                    // wait again for images to load for newly injected items
                    await this.waitForImagesLoaded(3000)
                    total = track.scrollWidth || 0
                    half = Math.ceil(total / 2)
                }

                // add 1px buffer to avoid tiny gaps due to subpixel rounding
                const final = Math.ceil(half) + 1
                // subtract a small overlap so the end slightly overlaps the start and hides seams
                const used = Math.max(1, final - (this.overlap || 0))
                // store positive pixel distance for JS animation
                this.marqueeDistance = used
                // set CSS var as well for any fallback visuals (use the used distance)
                track.style.setProperty('--marquee-distance', `${used}px`)

                // reset offset so animation restarts from 0
                this.offset = 0
                this.lastTs = null
                // apply initial transform (use translate3d and integer pixels for smoother rendering)
                track.style.transform = 'translate3d(0px,0,0)'

                // restart animation after measurement
                this.startAnimation()
                return Promise.resolve()
            },
            handleMouseEnter() {
                this.paused = true
                this.stopAnimation()
            },
            handleMouseLeave() {
                this.paused = false
                this.startAnimation()
            },
            startAnimation() {
                if (this.rafId) return
                this.lastTs = null
                const loop = (ts) => {
                    if (this.paused) {
                        this.lastTs = ts
                        this.rafId = requestAnimationFrame(loop)
                        return
                    }
                    if (this.lastTs == null) this.lastTs = ts
                    const delta = ts - this.lastTs
                    this.lastTs = ts
                    // speed is seconds per full loop (move marqueeDistance pixels)
                    const pxPerMs = this.marqueeDistance > 0 ? (this.marqueeDistance / (this.speed * 1000)) : 0
                    this.offset += pxPerMs * delta
                    // wrap using floor to handle large deltas
                    if (this.marqueeDistance > 0 && this.offset >= this.marqueeDistance) {
                        this.offset = this.offset - this.marqueeDistance * Math.floor(this.offset / this.marqueeDistance)
                    }
                    const track = this.$refs.track
                    if (track) {
                        const pos = -Math.round(this.offset)
                        track.style.transform = `translate3d(${pos}px,0,0)`
                    }
                    this.rafId = requestAnimationFrame(loop)
                }
                this.rafId = requestAnimationFrame(loop)
            },
            stopAnimation() {
                if (this.rafId) {
                    cancelAnimationFrame(this.rafId)
                    this.rafId = null
                }
            }
        }
    }
</script>

<style scoped>
    .marquee {
        width: 100%;
        overflow: hidden;
        display: block;
    }

    .marquee-track {
        display: flex;
        align-items: center;
        gap: 36px;
        height: 100%;
        min-width: max-content;

        /* ensure track doesn't collapse */
        white-space: nowrap;
        will-change: transform;
    }

    .marquee-item {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
    }

    .marquee-item img {
        max-height: 100%;
        height: auto;
        width: auto;
        display: block;
        object-fit: contain;
        filter: saturate(1);
    }

    @keyframes marquee-scroll {
        0% {
            transform: translateX(0);
        }

        100% {
            /* move left by exactly one set width (pixels) for seamless loop */
            transform: translateX(var(--marquee-distance, 0));
        }
    }

    /* Responsive tweaks */
    @media (max-width: 600px) {
        .marquee-track {
            gap: 18px;
        }
    }
</style>
